// React imports
import React from 'react';
import { Controller } from 'react-hook-form';
import { NavLink } from 'react-router-dom';

// Material-UI imports
import {
  Box,
  Button,
  Container,
  CircularProgress,
  CssBaseline,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import { VisibilityOffOutlined, VisibilityOutlined } from '@material-ui/icons';
// @ts-ignore
import Alert from '@material-ui/lab/Alert';

// Other imports

// Absolute imports
import Logo from 'assets/Diligent_Equity_Logo.png';
import { routes } from 'routes';
import getVersion from 'utils/version';

// Relative imports
import useStyles from './LoginStyles';
import { useLoginPage } from './useLoginPage';

export function LoginPage(props: { query: string }) {

  const classes = useStyles();
  const logic = useLoginPage(props.query);

  const hidePassword =
    logic?.authType !== null
      ? [logic.AUTH_TYPES.SSO, logic.AUTH_TYPES.PLATFORM_OIDC].includes(
          logic.authType,
        )
      : false;

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Paper variant="outlined" square className={classes.paper}>
        <img src={Logo} alt="logo" className={classes.logo} />
        <Box hidden={logic.authType === logic.AUTH_TYPES.PASSWORD_CHANGE}>
          <form className={classes.form} onSubmit={logic.getOnSubmitLogin()}>
            <label className={classes.inputLabel}>Email</label>
            <Controller
              name="email"
              control={logic.formLogin.control}
              render={(props) => (
                <TextField
                  {...props}
                  error={!!logic.formLogin.errors.email}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  autoComplete="email"
                  autoFocus
                  helperText={logic.formLogin.errors.email?.message || ' '}
                  classes={{ root: classes.formControl }}
                  disabled={logic.loginInProgress}
                />
              )
              }
            />
            {!hidePassword && (
              <>
                <label className={classes.inputLabel}>Password</label>
                <Controller
                  name="password"
                  control={logic.formLogin.control}
                  render={(props) => (
                    <TextField
                      {...props}
                      error={!!logic.formLogin.errors.password}
                      variant="outlined"
                      margin="normal"
                      fullWidth
                      type={logic.showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      helperText={logic.formLogin.errors.password?.message || ' '}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={logic.toggleShowPassword}
                              edge="end"
                            >
                              {logic.showPassword ? (
                                <VisibilityOutlined />
                              ) : (
                                <VisibilityOffOutlined />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      classes={{ root: classes.formControl }}
                      disabled={logic.loginInProgress}
                    />
                  )}
                />
                {logic.errorMessage && <Alert severity="error">{logic.errorMessage}</Alert>}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                  disabled={logic.loginMetaLoading || logic.loginInProgress}
                >
                  {logic.loginMetaLoading || logic.loginInProgress ? (
                    <CircularProgress />
                  ) : (
                    'Sign In'
                  )}
                </Button>
                <Box>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    align="center"
                  >
                    <NavLink
                      to={routes.PASSWORD_RESET}
                      color="textSecondary"
                      className={classes.link}
                    >
                      Forgot password?
                    </NavLink>
                  </Typography>
                </Box>
              </>
            )}
            {logic.authType === logic.AUTH_TYPES.SSO && (
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                className={classes.sso}
                onClick={logic.loginSso}
              >
                Single Sign On
              </Button>
            )}
            {logic.authType === logic.AUTH_TYPES.PLATFORM_OIDC && (
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                className={classes.sso}
                onClick={logic.loginPlatformOidc}
              >
                Login With Platform
              </Button>
            )}
          </form>
        </Box>
        {logic.authType === logic.AUTH_TYPES.PASSWORD_CHANGE && (
          <form className={classes.form} onSubmit={logic.getOnSubmitPasswordChange()}>
            <Box pt={0} pb={2}>
              <Typography variant="h5" align="center">Password Expired</Typography>
            </Box>
            <Typography variant="body2" gutterBottom>
              Your password has expired. Please create a new password.
              In order to protect your account, make sure your password complies with the below requirements:
            </Typography>
            <ul className={classes.passwordRequirements}>
              <li>at least 8 characters</li>
              <li>at least 1 number</li>
              <li>at least 1 upper case letter</li>
              <li>at least 1 lower case letter</li>
            </ul>
            <label className={classes.inputLabel}>New Password</label>
            <Controller
              name="password1"
              control={logic.formPasswordChange.control}
              render={(props) => (
                <TextField
                  {...props}
                  error={!!logic.formPasswordChange.errors.password1}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  type={logic.showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  helperText={logic.formPasswordChange.errors.password1?.message || ' '}
                  classes={{ root: classes.formControl }}
                />
              )}
            />
            <label className={classes.inputLabel}>Re-enter New Password</label>
            <Controller
              name="password2"
              control={logic.formPasswordChange.control}
              render={(props) => (
                <TextField
                  {...props}
                  error={!!logic.formPasswordChange.errors.password2}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  type={logic.showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  helperText={logic.formPasswordChange.errors.password2?.message || ' '}
                  classes={{ root: classes.formControl }}
                />
              )}
            />

            {logic.errorMessage && <Alert severity="error">{
              Array.isArray(logic.errorMessage)
                ? (
                  <ul className={classes.errorArray}>
                    {logic.errorMessage.map(msg => <li>{msg}</li>)}
                  </ul>
                )
                : logic.errorMessage
            }</Alert>}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submitPasswordChange}
              disabled={logic.loginInProgress}
            >
              {logic.loginInProgress ? (
                <CircularProgress />
              ) : (
                'Change Password'
              )}
            </Button>
          </form>
        )}
        <Box pt={1} width={'100%'} style={{ display: "flex", justifyContent: "space-between" }}>
          <Typography
            variant="body2"
            color="textPrimary"
            className={classes.privacyStatement}
            onClick={() => {
              // @ts-ignore
              window.open(
                "https://www.diligent.com/privacy/", "_blank");

            }}
          >
            Privacy statement
          </Typography>
          <Typography
            variant="body2"
            color="textPrimary"
            className={classes.version}
            onClick={() => { }}
          >
            {getVersion()}
          </Typography>
        </Box>
      </Paper>
    </Container >
  );
}